import { createSlice, createEntityAdapter } from '@reduxjs/toolkit';
import {
	getAccounts,
	getAllBidSales,
	getAllBidSalesDetails,
	getSearchRefreshAssessment,
	updateFiltersAndFetchBidSales,
	updateSortingAndFetchBidSales,
	updatePageAndFetchBidSales,
	getClosedMarketplaces,
	getScopeableEntities,
	getDealerships,
	getUsers,
	createDiscountApprovalRequest,
} from './dealDriver.thunks';
import { FILTERS_STORAGE_KEY, initialState, SORTING_KEYS } from './constants';
import { persistNewDealIds } from './helpers';
import { DEAL_WORKER_ALL_FILTER } from 'partials/admin/dealworking/Header/constants';
import { REQUEST_TYPES } from 'components/DealDriver/DiscountsModal/constants';

// Create an entity adapter for bidSales
export const bidSalesAdapter = createEntityAdapter();

export const dealDriverSlice = createSlice({
	name: 'dealDriver',
	initialState: {
		...initialState,
		data: bidSalesAdapter.getInitialState(), // Use entity adapter's initial state for data
	},
	reducers: {
		updateBidSale: (state, { payload }) => {
			const { id, changes } = payload;
			bidSalesAdapter.updateOne(state.data, { id, changes });
		},
		updateFilters: (state, { payload }) => {
			state.filters = { ...state.filters, ...payload };
			state.newIds.data = [];
			state.newIds.success = undefined;
		},
		updateSorting: (state, { payload }) => {
			state.sorting = payload;
			state.newIds.data = [];
			state.newIds.success = undefined;
		},
		updateDealershipsFormData: (state, { payload }) => {
			state.dealerships.formData = { ...state.dealerships.formData, ...payload };
		},
		removeSelectedDealership: (state, { payload }) => {
			state.dealerships.selectedDealerships = state.dealerships.selectedDealerships.filter(
				(dealership) => dealership.id !== payload,
			);
		},
		addSelectedDealership: (state, { payload }) => {
			const { id } = payload;
			if (!state.dealerships.selectedDealerships.find((dealership) => dealership.id === id)) {
				state.dealerships.selectedDealerships.push(payload);
			}
		},
		resetDealershipsResults: (state) => {
			state.dealerships.data = initialState.dealerships.data;
			state.dealerships.formData = initialState.dealerships.formData;
		},
		resetDealershipsData: (state) => {
			state.dealerships = initialState.dealerships;
		},
		updateSelectedDealerships: (state, { payload }) => {
			state.dealerships.selectedDealerships = payload;
		},
		updateAccountsFormData: (state, { payload }) => {
			state.accounts.formData = { ...state.accounts.formData, ...payload };
		},
		addSelectedAccount: (state, { payload }) => {
			const { id } = payload;
			if (!state.accounts.selectedAccounts.find((account) => account.id === id)) {
				state.accounts.selectedAccounts.push(payload);
			}
		},
		removeSelectedAccount: (state, { payload }) => {
			state.accounts.selectedAccounts = state.accounts.selectedAccounts.filter(
				(account) => account.id !== payload,
			);
		},
		resetAccountsData: (state) => {
			state.accounts = initialState.accounts;
		},
		updateSelectedAccounts: (state, { payload }) => {
			state.accounts.selectedAccounts = payload;
		},
		resetAccountsResults: (state) => {
			state.accounts.data = initialState.accounts.data;
			state.accounts.formData = initialState.accounts.formData;
		},
		updateUsersFormData: (state, { payload }) => {
			state.users.formData = { ...state.users.formData, ...payload };
		},
		addSelectedUser: (state, { payload }) => {
			const { id } = payload;
			if (!state.users.selectedUsers.find((user) => user.id === id)) {
				state.users.selectedUsers.push(payload);
			}
		},
		removeSelectedUser: (state, { payload }) => {
			state.users.selectedUsers = state.users.selectedUsers.filter((user) => user.id !== payload);
		},
		resetUsersData: (state) => {
			state.users = initialState.users;
		},
		updateSelectedUsers: (state, { payload }) => {
			state.users.selectedUsers = payload;
		},
		resetUsersResults: (state) => {
			state.users.data = initialState.users.data;
			state.users.formData = initialState.users.formData;
		},
		updateScopeableEntitiesFormData: (state, { payload }) => {
			state.scopeableEntities.formData = { ...state.scopeableEntities.formData, ...payload };
		},
		updateSelectedScopeableEntityName: (state, { payload }) => {
			state.scopeableEntities.selectedEntityName = payload;
		},
		updateSelectedScopeableEntityType: (state, { payload }) => {
			state.scopeableEntities.selectedEntityType = payload;
		},
		resetScopeableEntitiesData: (state) => {
			state.scopeableEntities = initialState.scopeableEntities;
		},
		updateAllBidSales: (state, { payload }) => {
			const { changes } = payload;
			const bidSales = Object.values(state.data.entities);
			const updates = bidSales.map((bidSale) => ({ id: bidSale.id, changes }));
			bidSalesAdapter.updateMany(state.data, updates);
		},
		removeBidSaleAndUpdateCounters: (state, { payload }) => {
			const { id, newCounters } = payload;
			const { page_count, total_count, counters } = newCounters;
			state.metadata = { ...state.metadata, page_count, total_count };
			state.metadata.counters = { ...state.metadata.counters, ...counters };
			bidSalesAdapter.removeOne(state.data, id);
		},
		clearErrors: (state) => {
			state.errors = {
				dataErrorMessage: '',
				expandAllErrorMessage: '',
				scopeableEntitiesErrorMessage: '',
				accountsErrorMessage: '',
				dealershipsErrorMessage: '',
				usersErrorMessage: '',
				discountApprovalRequestErrorMessage: '',
			};
		},
		resetDiscountApprovalRequestState: (state) => {
			state.discountApprovalRequest.loading = false;
			state.discountApprovalRequest.success = undefined;
			state.errors.discountApprovalRequestErrorMessage = '';
		},
		updateNewIds: (state, { payload }) => {
			const { newIds } = payload;
			state.newIds.data = newIds;
			state.newIds.success = true;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getAllBidSales.pending, (state) => {
				state.loading = true;
				state.success = undefined;
				state.errors.dataErrorMessage = '';
			})
			.addCase(getAllBidSales.fulfilled, (state, { payload }) => {
				const { bidSales, metadata } = payload;
				const { newIds } = state;

				if (!Array.isArray(bidSales)) return;

				const currentIds = bidSales.map((b) => b.id);

				const bidSalesWithExpanded = bidSales.map((bidSale) => ({
					...bidSale,
					isExpanded: false,
					newDeal: newIds.data.length ? newIds.data.includes(bidSale.id) : false,
				}));

				bidSalesAdapter.setAll(state.data, bidSalesWithExpanded);
				state.metadata = metadata;
				state.loading = false;
				state.success = true;

				if (newIds.data.length && state.filters.deal_worker !== DEAL_WORKER_ALL_FILTER) {
					// Only remove IDs that are now visible
					const remainingNewDealIds = newIds.data.filter((id) => !currentIds.includes(id));
					state.newIds.data = remainingNewDealIds;

					persistNewDealIds(state.filters.deal_worker, remainingNewDealIds);
				}

				state.previousFilters = {
					...state.filters,
				};
			})
			.addCase(getAllBidSales.rejected, (state, { payload }) => {
				state.loading = false;
				state.success = false;
				state.errors.dataErrorMessage = payload || 'Something went wrong, please try again.';
			})
			.addCase(getAccounts.pending, (state) => {
				state.accounts.loading = true;
				state.accounts.success = undefined;
				state.errors.accountsErrorMessage = '';
			})
			.addCase(getAccounts.fulfilled, (state, { payload }) => {
				const { accounts, metadata } = payload;
				state.accounts.data = accounts;
				state.accounts.metadata = metadata;
				state.accounts.loading = false;
				state.accounts.success = true;
			})
			.addCase(getAccounts.rejected, (state, { payload }) => {
				state.accounts.loading = false;
				state.accounts.success = false;
				state.errors.accountsErrorMessage = payload || 'Something went wrong, please try again.';
			})
			.addCase(getUsers.pending, (state) => {
				state.users.loading = true;
				state.users.success = undefined;
				state.errors.usersErrorMessage = '';
			})
			.addCase(getUsers.fulfilled, (state, { payload }) => {
				const { users, metadata } = payload;
				state.users.data = users;
				state.users.metadata = metadata;
				state.users.loading = false;
				state.users.success = true;
			})
			.addCase(getUsers.rejected, (state, { payload }) => {
				state.users.loading = false;
				state.users.success = false;
				state.errors.usersErrorMessage = payload || 'Something went wrong, please try again.';
			})
			.addCase(getDealerships.pending, (state) => {
				state.dealerships.loading = true;
				state.dealerships.success = undefined;
				state.errors.dealershipsErrorMessage = '';
			})
			.addCase(getDealerships.fulfilled, (state, { payload }) => {
				const { dealerships, metadata } = payload;
				state.dealerships.data = dealerships;
				state.dealerships.metadata = metadata;
				state.dealerships.loading = false;
				state.dealerships.success = true;
			})
			.addCase(getDealerships.rejected, (state, { payload }) => {
				state.dealerships.loading = false;
				state.dealerships.success = false;
				state.errors.dealershipsErrorMessage = payload || 'Something went wrong, please try again.';
			})
			.addCase(getScopeableEntities.pending, (state) => {
				state.scopeableEntities.loading = true;
				state.scopeableEntities.success = undefined;
				state.errors.scopeableEntitiesErrorMessage = '';
			})
			.addCase(getScopeableEntities.fulfilled, (state, { payload }) => {
				const { scopeableEntities, metadata } = payload;
				state.scopeableEntities.data = scopeableEntities;
				state.scopeableEntities.metadata = metadata;
				state.scopeableEntities.loading = false;
				state.scopeableEntities.success = true;
			})
			.addCase(getScopeableEntities.rejected, (state, { payload }) => {
				state.scopeableEntities.loading = false;
				state.scopeableEntities.success = false;
				state.errors.scopeableEntitiesErrorMessage = payload || 'Something went wrong, please try again.';
			})
			.addCase(getAllBidSalesDetails.pending, (state) => {
				state.loading = true;
				state.success = undefined;
				state.errors.expandAllErrorMessage = '';
			})
			.addCase(getAllBidSalesDetails.fulfilled, (state, { payload }) => {
				const { bidSales } = payload;

				const updates = bidSales.map((bidSale) => ({
					id: bidSale.id,
					changes: {
						...bidSale,
						isExpanded: true,
					},
				}));

				bidSalesAdapter.updateMany(state.data, updates);

				state.loading = false;
				state.success = true;
			})
			.addCase(getAllBidSalesDetails.rejected, (state, { payload }) => {
				state.loading = false;
				state.success = false;
				state.errors.expandAllErrorMessage = payload || 'Something went wrong, please try again.';
			})
			.addCase(getSearchRefreshAssessment.pending, (state) => {
				state.newIds.success = undefined;
			})
			.addCase(getSearchRefreshAssessment.fulfilled, (state, { payload }) => {
				const data = payload;
				state.newIds.data = data || [];
				state.newIds.success = true;
			})
			.addCase(getSearchRefreshAssessment.rejected, (state) => {
				state.newIds.success = false;
			})
			.addCase(getClosedMarketplaces.pending, (state) => {
				state.closedMarketplaces.loading = true;
				state.closedMarketplaces.success = undefined;
			})
			.addCase(getClosedMarketplaces.fulfilled, (state, { payload }) => {
				const { closedMarketplaces } = payload;
				state.closedMarketplaces.data = closedMarketplaces;
				state.closedMarketplaces.loading = false;
				state.closedMarketplaces.success = true;
			})
			.addCase(getClosedMarketplaces.rejected, (state) => {
				state.closedMarketplaces.loading = false;
				state.closedMarketplaces.success = false;
			})
			.addCase(createDiscountApprovalRequest.pending, (state) => {
				state.discountApprovalRequest.loading = true;
				state.discountApprovalRequest.success = undefined;
				state.errors.discountApprovalRequestErrorMessage = '';
			})
			.addCase(createDiscountApprovalRequest.fulfilled, (state, { payload }) => {
				const { bidSaleId, responseData } = payload || {};
				state.discountApprovalRequest.loading = false;
				state.discountApprovalRequest.success = true;
				state.errors.discountApprovalRequestErrorMessage = '';

				const approvalRequest = responseData?.data?.approval_request;
				const requestType = approvalRequest?.request_type;
				const requestStatus = approvalRequest?.status;

				if (!bidSaleId || !requestType || !requestStatus) return;

				if (requestType === REQUEST_TYPES.buy_fee_discount) {
					bidSalesAdapter.updateOne(state.data, {
						id: bidSaleId,
						changes: { buy_fee_discount_status: requestStatus },
					});

					return;
				}

				if (requestType === REQUEST_TYPES.absolute_sale_guarantee) {
					bidSalesAdapter.updateOne(state.data, {
						id: bidSaleId,
						changes: { absolute_sale_guarantee_status: requestStatus },
					});
				}
			})
			.addCase(createDiscountApprovalRequest.rejected, (state) => {
				state.discountApprovalRequest.loading = false;
				state.discountApprovalRequest.success = false;
				state.errors.discountApprovalRequestErrorMessage = 'Failed to submit this request. Please try again.';
			});
	},
});

export const {
	updateBidSale,
	updateFilters,
	updateSorting,
	updateAllBidSales,
	removeBidSaleAndUpdateCounters,
	updateScopeableEntitiesFormData,
	updateSelectedScopeableEntityName,
	updateSelectedScopeableEntityType,
	resetScopeableEntitiesData,
	resetAccountsData,
	updateAccountsFormData,
	updateSelectedAccounts,
	addSelectedAccount,
	removeSelectedAccount,
	resetAccountsResults,
	clearErrors,
	resetDiscountApprovalRequestState,
	updateNewIds,
	updateDealershipsFormData,
	removeSelectedDealership,
	addSelectedDealership,
	resetDealershipsResults,
	resetDealershipsData,
	updateSelectedDealerships,
	updateUsersFormData,
	addSelectedUser,
	removeSelectedUser,
	resetUsersData,
	updateSelectedUsers,
	resetUsersResults,
} = dealDriverSlice.actions;

// Export selectors from the entity adapter
export const {
	selectIds: selectAllBidSalesIds,
	selectById: selectBidSaleById,
	selectEntities: selectBidSaleEntities,
} = bidSalesAdapter.getSelectors((state) => state.dealDriver.data);

export default dealDriverSlice.reducer;

export const selectDealDriverSyncConfig = () => ({
	storageKey: FILTERS_STORAGE_KEY,
	sortingKeys: SORTING_KEYS,
	filterActionCreator: updateFiltersAndFetchBidSales,
	sortingActionCreator: updateSortingAndFetchBidSales,
	pageActionCreator: updatePageAndFetchBidSales,
});

export const selectDealDriverFiltersOnlySyncConfig = () => ({
	storageKey: FILTERS_STORAGE_KEY,
	sortingKeys: SORTING_KEYS,
	filterActionCreator: updateFilters,
});
